import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useZodForm } from '../../../hooks/zod-form'
import { AppForm, FormCheckbox, FormSubmitButton, FormTextField } from '../../../components/FormCore'
import { ShowDialogProps } from '../../../show-dialog'
import { Box, Select, MenuItem, Grid } from '@mui/material'
import { CancelButton } from '../../../components/CancelButton'
import { IProductCategory } from '../../../generated-types/product-category'
import { ProductCategoryFilterType, ReportGroupingMode } from 'src/enums'
import { toReportGroupingMode } from './productCategory.action'

export interface ProductCategoryFormProps extends ShowDialogProps {
  onSubmit: (form: any) => Promise<void>
  initialValues?: IProductCategory
}

export const ProductCategoryForm: React.FC<ProductCategoryFormProps> = ({ onSubmit, pop, initialValues }) => {
  const { t: translate } = useTranslation(['common'])
  const schema = z.object({
    name: z.string().nonempty(translate('nameIsRequired', { ns: 'common' })),
    description: z.string().optional(),
    isServiceCategory: z.boolean().default(false),
    reportGroupingMode: z.nativeEnum(ReportGroupingMode).default(ReportGroupingMode.ByProduct),
    groupByPositionInPmReports: z.boolean().default(false),
    isIncludedInFilter: z.boolean().nullable().optional(),
    filterTypeID: z
      .nativeEnum(ProductCategoryFilterType)
      .default(ProductCategoryFilterType.IsFilterByCount)
      .nullable()
      .optional(), // Default value, can be changed by the user when isIncludedInFilter is true
  })

  const onCancel = () => {
    pop()
  }
  const isUpdateForm = initialValues?.id
  const form = useZodForm(schema, {
    defaultValues: {
      ...initialValues,
      filterTypeID: initialValues?.filterTypeID ?? ProductCategoryFilterType.IsFilterByCount,
      reportGroupingMode: initialValues?.reportGroupingMode ?? ReportGroupingMode.ByProduct,
      groupByPositionInPmReports: initialValues?.reportGroupingMode === ReportGroupingMode.ByPositionAndProduct,
    },
  })
  const getCategoryFilterTypeLabel = (value: ProductCategoryFilterType): string => {
    switch (value) {
      case ProductCategoryFilterType.IsFilterByCount:
        return translate('filter-by-count', { ns: 'product-category' })
      case ProductCategoryFilterType.IsCheckExist:
        return translate('filter-by-availability', { ns: 'product-category' })
      default:
        return String(value)
    }
  }

  const CategoryfilterOptions = Object.values(ProductCategoryFilterType)
    .filter(value => typeof value === 'number')
    .map(value => ({
      value: value as ProductCategoryFilterType,
      label: getCategoryFilterTypeLabel(value as ProductCategoryFilterType),
    }))

  const isIncludedInFilter = form.watch('isIncludedInFilter')

  const filterTypeID = form.watch('filterTypeID')

  const handleSubmit = async (values: z.infer<typeof schema>) => {
    const { groupByPositionInPmReports, ...categoryValues } = values
    await onSubmit({ ...categoryValues, reportGroupingMode: toReportGroupingMode(groupByPositionInPmReports) })
  }

  return (
    <AppForm form={form} onSubmit={handleSubmit}>
      {isUpdateForm && <FormTextField name="id" sx={{ display: 'none' }} />}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormTextField name="name" label={translate('name', { ns: 'common' })} fullWidth />
        </Grid>
        <Grid item xs={12}>
          <FormTextField
            name="description"
            label={translate('description', { ns: 'common' })}
            fullWidth
            multiline
            rows={4}
          />
        </Grid>
        {/* Row with checkboxes and dropdown */}
        <Grid item xs={12}>
          <Box className="flex items-center gap-4">
            <FormCheckbox name="isServiceCategory" label={translate('isServiceCategory', { ns: 'product-category' })} />
            <FormCheckbox
              name="groupByPositionInPmReports"
              label={translate('group-by-positions-in-pm-reports', { ns: 'product-category' })}
            />
            <FormCheckbox
              name="isIncludedInFilter"
              label={translate('include-in-filter', { ns: 'product-category' })}
            />

            {isIncludedInFilter && (
              <Select
                name="filterTypeID"
                value={filterTypeID ?? CategoryfilterOptions[0]?.value}
                onChange={event => {
                  form.setValue('filterTypeID', Number(event.target.value))
                }}
                size="small"
              >
                {CategoryfilterOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
        </Grid>
      </Grid>
      <Box className="flex justify-end gap-4 pt-4">
        <CancelButton onClick={onCancel}>{translate('cancel', { ns: 'common' })}</CancelButton>
        <FormSubmitButton
          createText={isUpdateForm ? translate('update', { ns: 'common' }) : translate('add', { ns: 'common' })}
        />
      </Box>
    </AppForm>
  )
}
